import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/client';
import { GmailPagination } from '@/components/ui/GmailPagination';

export interface WaMessageRow {
  id: string; merchantId: number; merchant: string | null; orderType: string | null;
  waybill: string | null; awbNo: string | null; lrNo: string | null;
  event: string; eventLabel: string; templateName: string | null; mobile: string | null;
  status: string; skipReason: string | null; error: string | null; replyText: string | null;
  billingMode: string | null; charged: boolean; refunded: boolean;
  price: number; gstAmount: number; totalCharge: number; invoiceId: number | null;
  createdAt: string; sentAt: string | null; deliveredAt: string | null; readAt: string | null; failedAt: string | null;
}
interface History {
  total: number; page: number; pageSize: number;
  summary: { total: number; counts: Record<string, number>; billedMessages: number; charged: number; chargedExGst: number; gst: number };
  items: WaMessageRow[];
}

export const WA_STATUS_CHIP: Record<string, string> = {
  SENT: 'bg-sky-50 text-sky-700 border-sky-200',
  DELIVERED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  READ: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  FAILED: 'bg-rose-50 text-rose-700 border-rose-200',
  SKIPPED: 'bg-amber-50 text-amber-700 border-amber-200',
  QUEUED: 'bg-slate-50 text-slate-600 border-slate-200',
};
const EVENTS = [['', 'All events'], ['BOOKING', 'Booking'], ['DELIVERED', 'Delivered'], ['FAILED', 'Delivery Failed'], ['FEEDBACK', 'Feedback'], ['INVOICE', 'Invoice']];
const STATUSES = ['', 'SENT', 'DELIVERED', 'READ', 'FAILED', 'SKIPPED'];
const rs = (n: number) => `₹${Number(n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata' }) : '—');

/**
 * Every WhatsApp message, searchable by AWB / LR — with what it cost. Staff see all merchants (or one,
 * with `merchantId`); a merchant login is pinned to its own messages by the API whatever it sends.
 */
export function WhatsappHistory({ merchantId, showMerchant = false }: { merchantId?: number; showMerchant?: boolean }) {
  const [q, setQ] = useState('');
  const [applied, setApplied] = useState('');
  const [event, setEvent] = useState('');
  const [status, setStatus] = useState('');
  const [orderType, setOrderType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const pageSize = 25;
  const params: Record<string, any> = { q: applied || undefined, event: event || undefined, status: status || undefined, orderType: orderType || undefined, from: from || undefined, to: to || undefined, merchantId };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['whatsapp', 'messages', params, page],
    queryFn: async () => (await api.get<History>('/whatsapp/messages', { params: { ...params, page, pageSize } })).data,
    placeholderData: (p) => p,
  });

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await api.get('/whatsapp/messages/export', { params, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a'); a.href = url; a.download = `whatsapp-messages-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch { toast.error('Could not export'); } finally { setExporting(false); }
  };

  const s = data?.summary;
  const reset = () => setPage(1);
  return (
    <div className="space-y-3" data-testid="wa-history">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card label="Messages" value={s ? s.total.toLocaleString('en-IN') : '—'} />
        <Card label="Sent / delivered / read" value={s ? ((s.counts.SENT ?? 0) + (s.counts.DELIVERED ?? 0) + (s.counts.READ ?? 0)).toLocaleString('en-IN') : '—'} />
        <Card label="Failed" value={s ? String(s.counts.FAILED ?? 0) : '—'} tone={s && (s.counts.FAILED ?? 0) > 0 ? 'bad' : undefined} />
        <Card label="Not sent" value={s ? String(s.counts.SKIPPED ?? 0) : '—'} sub="no balance / no mobile / no template" />
        <Card label="Charged (incl. GST)" value={s ? rs(s.charged) : '—'} sub={s ? `${s.billedMessages} billed · GST ${rs(s.gst)}` : undefined} />
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="flex items-center gap-1 border border-slate-300 rounded-lg px-2 bg-white">
          <Search size={14} className="text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { setApplied(q.trim()); reset(); } }}
            placeholder="AWB / LR / invoice no (several: comma or space)" className="py-1.5 text-sm outline-none w-64" data-testid="wa-search" />
        </div>
        <button onClick={() => { setApplied(q.trim()); reset(); }} className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50">Search</button>
        <select value={event} onChange={(e) => { setEvent(e.target.value); reset(); }} className="input py-1.5 text-sm w-40">
          {EVENTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={status} onChange={(e) => { setStatus(e.target.value); reset(); }} className="input py-1.5 text-sm w-36">
          {STATUSES.map((v) => <option key={v} value={v}>{v ? (v === 'SKIPPED' ? 'Not sent' : v.charAt(0) + v.slice(1).toLowerCase()) : 'All statuses'}</option>)}
        </select>
        <select value={orderType} onChange={(e) => { setOrderType(e.target.value); reset(); }} className="input py-1.5 text-sm w-28">
          <option value="">B2C + B2B</option><option value="B2C">B2C</option><option value="B2B">B2B</option>
        </select>
        <label className="text-xs text-slate-500">From<input type="date" value={from} onChange={(e) => { setFrom(e.target.value); reset(); }} className="input py-1 text-sm block" /></label>
        <label className="text-xs text-slate-500">To<input type="date" value={to} onChange={(e) => { setTo(e.target.value); reset(); }} className="input py-1 text-sm block" /></label>
        <button onClick={exportCsv} disabled={exporting} className="ml-auto px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 flex items-center gap-1 disabled:opacity-50">
          {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Export CSV
        </button>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-x-auto bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Date (IST)</th>
              {showMerchant && <th className="text-left px-3 py-2">Merchant</th>}
              <th className="text-left px-3 py-2">AWB / LR / Invoice</th>
              <th className="text-left px-3 py-2">Event</th>
              <th className="text-left px-3 py-2">Template</th>
              <th className="text-left px-3 py-2">Mobile</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Charge</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={8} className="text-center py-6 text-slate-400"><Loader2 size={14} className="animate-spin inline" /> Loading…</td></tr>
            ) : !data?.items.length ? (
              <tr><td colSpan={8} className="text-center py-6 text-slate-400">No WhatsApp messages{applied ? ` for ${applied}` : ''}.</td></tr>
            ) : data.items.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 align-top">
                <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{fmt(r.createdAt)}</td>
                {showMerchant && <td className="px-3 py-2 text-xs">{r.merchant ?? `#${r.merchantId}`}</td>}
                <td className="px-3 py-2 font-mono text-xs">{r.waybill ?? '—'}<div className="text-[10px] text-slate-400 font-sans">{r.orderType}</div></td>
                <td className="px-3 py-2 text-xs">{r.eventLabel}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{r.templateName ?? '—'}</td>
                <td className="px-3 py-2 text-xs font-mono">{r.mobile ?? '—'}</td>
                <td className="px-3 py-2 text-xs max-w-[280px]">
                  <span className={`inline-block border rounded-full px-2 py-0.5 text-[11px] font-medium ${WA_STATUS_CHIP[r.status] ?? WA_STATUS_CHIP.QUEUED}`}>
                    {r.status === 'SKIPPED' ? 'Not sent' : r.status.charAt(0) + r.status.slice(1).toLowerCase()}
                  </span>
                  {(r.skipReason || r.error) && <div className="text-[11px] text-slate-500 mt-0.5">{r.skipReason ?? r.error}</div>}
                  {r.replyText && <div className="text-[11px] text-indigo-700 mt-0.5">Reply: {r.replyText}</div>}
                </td>
                <td className="px-3 py-2 text-right text-xs whitespace-nowrap">
                  {r.charged ? <span className="font-medium">{rs(r.totalCharge)}</span> : <span className="text-slate-400">{r.refunded ? 'refunded' : '—'}</span>}
                  {r.charged && <div className="text-[10px] text-slate-400">{rs(r.price)} + GST · {r.billingMode === 'POSTPAID' ? (r.invoiceId ? 'invoiced' : 'to invoice') : 'wallet'}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end items-center gap-2">
        {isFetching && <Loader2 size={12} className="animate-spin text-slate-400" />}
        <GmailPagination page={page} pageSize={pageSize} total={data?.total ?? 0} onPage={setPage} />
      </div>
    </div>
  );
}

export function Card({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'bad' | 'good' }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-lg font-semibold mt-0.5 ${tone === 'bad' ? 'text-danger' : tone === 'good' ? 'text-emerald-700' : 'text-slate-900'}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}
